import { Fragment } from "react"
import { Chart, useChart } from "@chakra-ui/charts"
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from "recharts"
import { Box, Heading, HStack, VStack } from "@chakra-ui/react"
import { FaCarBattery, FaGasPump } from "react-icons/fa6"
import type { IconType } from "react-icons"
import Informative from "@hydrologiq/shared/Informative"
import { unitTooltip } from "./ChartTooltip"
import { DEFAULT_FREQUENCY, type Frequency } from "./frequency"
import { reportDateFormatter } from "./reportDateFormat"
import { LABEL_TEXT_COLOR, SEMANTIC_COLORS } from "./reportColors"
import {
  AVG_CSS,
  CAPTURE,
  CAPTURE_CHART_TEXT_CSS,
  CAPTURE_HEADER_MIN_HEIGHT,
  TOTAL_CSS,
  WIDE,
} from "./chartHeaderTags"

/**
 * Structural types mirroring the generated `getReport` response shape
 * (`ReportGraph` / `ReportSeries` / `SeriesPoint` from
 * `@hydrologiq/gql-experience/graphql`). Declared locally (as in
 * `PhaseBalanceChart`) so this component stays decoupled from codegen while
 * remaining structurally assignable from the generated types — `Report.tsx`
 * hands a `ReportGraph` straight in as `graph`.
 */
export interface RunHoursSeriesPoint {
  time: string
  value?: number | null
  peak?: number | null
}

export interface RunHoursSeries {
  key: string
  label?: string | null
  points: RunHoursSeriesPoint[]
}

export interface RunHoursGraph {
  title?: string | null
  axis?: string | null
  series: RunHoursSeries[]
}

export interface RunHoursChartProps {
  /** A `ReportGraph`-like object carrying the `engine_hours` / `battery_only_hours` series. */
  graph: RunHoursGraph
  /** Chart heading; defaults to the graph title, then "Run hours". */
  title?: string
  /**
   * Report cadence, so sub-daily buckets get time-bearing tick/hover labels
   * rather than a repeated day. Defaults to {@link DEFAULT_FREQUENCY}.
   */
  frequency?: Frequency
}

/** The two legs of the stack. */
export type RunHoursLeg = "engine" | "battery"

/** Wire series key → stack leg. */
const RUN_HOURS_KEYS: ReadonlyMap<string, RunHoursLeg> = new Map([
  ["engine_hours", "engine"],
  ["battery_only_hours", "battery"],
])

/** The stack leg a series key feeds, or `null` for any other key. */
export function legForKey(key: string): RunHoursLeg | null {
  return RUN_HOURS_KEYS.get(key) ?? null
}

/** Stack order (bottom → top), colours, legend labels and icons per leg. */
const LEG_ORDER: readonly RunHoursLeg[] = ["engine", "battery"]

const LEG_CONFIG: Record<RunHoursLeg, { color: string; label: string; icon: IconType }> = {
  engine: { color: SEMANTIC_COLORS.generator, label: "Generator running", icon: FaGasPump },
  battery: { color: SEMANTIC_COLORS.battery, label: "Battery only", icon: FaCarBattery },
}

const STACK_ID = "run-hours"

/** Round to one decimal place, matching the other report graphs' display. */
function round1(value: number): number {
  return Math.round(value * 10) / 10
}

export interface RunHoursDatum {
  start: string
  engine?: number | null
  battery?: number | null
}

/**
 * Fold the two run-hours series into one datum per time bucket:
 * `{ start, engine, battery }`. Absent points stay `null` (a gap) rather than
 * being coerced to 0. Series with an unrecognised key are ignored.
 */
export function toStackedData(series: RunHoursSeries[]): RunHoursDatum[] {
  const byBucket = new Map<string, RunHoursDatum>()

  for (const s of series) {
    const leg = legForKey(s.key)
    if (!leg) continue
    for (const point of s.points ?? []) {
      const existing = byBucket.get(point.time) ?? { start: point.time }
      existing[leg] = point.value ?? null
      byBucket.set(point.time, existing)
    }
  }

  return Array.from(byBucket.values()).sort((a, b) => a.start.localeCompare(b.start))
}

/** The Y axis always spans at least a full day for daily-and-coarser buckets. */
export const RUN_HOURS_AXIS_MIN_MAX = 24

/** For sub-daily (hourly / 5-minute) buckets a bucket is at most 1 h, so the axis spans one hour. */
export const RUN_HOURS_SUB_DAILY_AXIS_MAX = 1

export interface RunHoursAxis {
  /** Upper bound of the axis in hours; at least 1 (sub-daily) or {@link RUN_HOURS_AXIS_MIN_MAX}. */
  max: number
  /** Tick values from 0 to `max`, evenly spaced. */
  ticks: number[]
}

/** True when each bucket covers at most an hour (hourly or 5-minute reports). */
function isSubDaily(frequency: Frequency): boolean {
  return frequency === "hourly" || frequency === "5min"
}

/**
 * The Y axis range.
 *
 * - **Sub-daily buckets** (an hourly or 5-minute report, e.g. a single-day
 *   report): pinned to `0..1 h` in quarter-hour steps, since no bucket can run
 *   longer than an hour. A 24 h scale would flatten every bar.
 * - **Daily and coarser**: pinned to `0..24`, extended only when a bucket's stack
 *   (engine + battery) is taller than that, e.g. a weekly or monthly bucket. The
 *   extended maximum is rounded up to a whole number of tick steps, and the step
 *   doubles (6, 12, 24, 48, ...) to keep the tick count to at most 6 intervals.
 *
 * A sub-daily stack taller than 1 h (unexpected) falls back to the daily logic
 * so bars are never clipped.
 */
export function runHoursAxis(data: RunHoursDatum[], frequency: Frequency = DEFAULT_FREQUENCY): RunHoursAxis {
  const tallest = data.reduce((max, d) => Math.max(max, (d.engine ?? 0) + (d.battery ?? 0)), 0)

  // Small epsilon so float sums like 1.0000000000000002 don't trip the fallback.
  if (isSubDaily(frequency) && tallest <= RUN_HOURS_SUB_DAILY_AXIS_MAX + 1e-9) {
    return { max: RUN_HOURS_SUB_DAILY_AXIS_MAX, ticks: [0, 0.25, 0.5, 0.75, 1] }
  }

  const needed = Math.max(RUN_HOURS_AXIS_MIN_MAX, Math.ceil(tallest))

  let step = 6
  while (needed / step > 6) step *= 2

  const max = Math.ceil(needed / step) * step
  const ticks: number[] = []
  for (let tick = 0; tick <= max; tick += step) ticks.push(tick)
  return { max, ticks }
}

/** Header statistics for one leg of the chart. */
export interface RunHoursLegStats {
  /** True when the leg has at least one present (non-null) point value. */
  present: boolean
  /** Sum of the leg's present values (hours). */
  total: number
  /** `total / presentBucketCount`; 0 when the leg has no present values. */
  avg: number
  /** `legTotal / (engineTotal + batteryTotal) × 100`; 0 for a zero denominator. */
  percent: number
}

export type RunHoursStats = Record<RunHoursLeg, RunHoursLegStats>

/**
 * Per-leg header statistics (the cross-series cousin of `graphSeriesStats`,
 * which folds every series into one total). Computed client-side from the same
 * points the backend returned, so the figures stay in lockstep with the bars.
 *
 * The percentage denominator is `engineTotal + batteryTotal` — *active* hours,
 * not elapsed — because the backend already excludes idle buckets from both
 * legs. A zero denominator yields 0 (never NaN); a single present leg with a
 * positive total is therefore 100 %.
 */
export function runHoursStats(graph: Pick<RunHoursGraph, "series">): RunHoursStats {
  const sums: Record<RunHoursLeg, { total: number; count: number }> = {
    engine: { total: 0, count: 0 },
    battery: { total: 0, count: 0 },
  }

  for (const s of graph.series ?? []) {
    const leg = legForKey(s.key)
    if (!leg) continue
    for (const point of s.points ?? []) {
      if (point.value == null) continue
      sums[leg].total += point.value
      sums[leg].count += 1
    }
  }

  const denominator = sums.engine.total + sums.battery.total
  const toStats = ({ total, count }: { total: number; count: number }): RunHoursLegStats => ({
    present: count > 0,
    total,
    avg: count > 0 ? total / count : 0,
    percent: denominator > 0 ? (total / denominator) * 100 : 0,
  })

  return { engine: toStats(sums.engine), battery: toStats(sums.battery) }
}

/**
 * The header tags scale with the CARD's width, not the viewport: the card is a
 * size container, so a half-width grid cell, the PDF cell and a phone all pick
 * the right layout. Below this width the legs stack (right-aligned, horizontal
 * divider, compact pills); at or above it they sit side by side (right-aligned,
 * vertical divider, roomier pills). Where container queries are unsupported the
 * compact stacked layout is used, which is always correct.
 */
// Pill styling is shared with the other report charts (see chartHeaderTags.ts).
const PERCENT_CSS = {
  fontWeight: 700,
  color: "#2b2b2b",
  whiteSpace: "nowrap",
  fontSize: "13px",
  [WIDE]: { fontSize: "14px" },
  [CAPTURE]: { fontSize: "9px" },
} as const


/**
 * One leg's header tag group: an icon (no text label), the bold % share, then
 * the Avg and Total pills. The icon carries an accessible name instead of
 * visible text. Right-aligned, and the pills wrap between (never inside) pills
 * if the card is narrower than the group.
 */
function HeaderTagGroup({ leg, stats, unit }: { leg: RunHoursLeg; stats: RunHoursLegStats; unit: string }) {
  const { color, label, icon: LegIcon } = LEG_CONFIG[leg]
  const withUnit = (value: number) => (unit ? `${round1(value)} ${unit}` : `${round1(value)}`)

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="wrap"
      gap={2}
      css={{ [CAPTURE]: { gap: "4px", "& svg": { width: "11px", height: "11px" } } }}
      data-run-hours-tag-group={leg}
    >
      <LegIcon role="img" aria-label={label} color={color} size={16} />
      <Box as="span" css={PERCENT_CSS}>
        {Math.round(stats.percent)}%
      </Box>
      <Box as="span" css={AVG_CSS}>
        Avg {withUnit(stats.avg)}
      </Box>
      <Box as="span" css={TOTAL_CSS}>
        Total {withUnit(stats.total)}
      </Box>
    </Box>
  )
}

/**
 * Divider between the two legs: a horizontal rule when the legs are stacked and
 * a vertical rule when they sit side by side.
 */
function LegDivider() {
  return (
    <Box
      role="separator"
      alignSelf="stretch"
      css={{
        borderTop: "1px solid #e2e2e2",
        [WIDE]: { borderTop: 0, borderLeft: "1px solid #e2e2e2", minHeight: "24px" },
        [CAPTURE]: { borderTop: 0, borderLeft: "1px solid #e2e2e2", minHeight: "16px" },
      }}
    />
  )
}

/** Legend entry for a present leg: coloured icon plus its label. */
function LegendItem({ leg }: { leg: RunHoursLeg }) {
  const { color, label, icon: LegIcon } = LEG_CONFIG[leg]
  return (
    <HStack
      as="li"
      gap={1}
      fontSize="xs"
      color={LABEL_TEXT_COLOR}
      listStyleType="none"
      whiteSpace="nowrap"
      css={{ [CAPTURE]: { fontSize: "9px" } }}
    >
      <LegIcon aria-hidden="true" color={color} size={12} />
      <span>{label}</span>
    </HStack>
  )
}

/**
 * Stacked "Run hours" chart: generator-running hours (`engine_hours`, purple)
 * stacked with battery-only hours (`battery_only_hours`, green), with per-leg
 * header statistics. Modelled on {@link PhaseBalanceChart}.
 *
 * The two legs are mutually exclusive and sum to *active* hours (≤ elapsed). The
 * Y axis is always pinned to at least 0–24 h so bars read against a full day, and
 * only extends beyond 24 h when a bucket's stack is taller (see {@link runHoursAxis}).
 */
export function RunHoursChart({ graph, title, frequency = DEFAULT_FREQUENCY }: RunHoursChartProps) {
  const data = toStackedData(graph.series ?? [])
  const axis = runHoursAxis(data, frequency)
  const stats = runHoursStats(graph)
  const unit = graph.axis ?? "h"

  const chart = useChart<RunHoursDatum>({
    data,
    series: LEG_ORDER.map((leg) => {
      const config = LEG_CONFIG[leg]
      return { name: leg, color: config.color, label: config.label, stackId: STACK_ID }
    }),
  })

  const dateFormatter = reportDateFormatter(frequency)
  const heading = title ?? graph.title ?? "Run hours"

  const presentLegs = LEG_ORDER.filter((leg) => stats[leg].present)
  const hasData = presentLegs.length > 0

  return (
    <Box bg="card" boxShadow="card" borderRadius="card" p={4} width="100%" height="100%">
      {/* Size container for the header tag layout (see TAGS_ROW_MIN_WIDTH). Kept
          on an inner wrapper so the card's own style props stay untouched. */}
      <Box css={{ containerType: "inline-size", width: "100%", height: "100%" }}>
        <VStack pt={1} pb={2} gap={3} alignItems={"flex-start"} justifyContent="space-between" width="100%" height="100%">
          {/* Header: heading on the left, the per-leg tag groups on the right,
            right-aligned. The row wraps, so when the card is too narrow for both
            the tags drop under the heading and stay right-aligned. Inside the
            stack the legs go side by side with a vertical divider when the card
            is wide enough, and stack with a horizontal divider when it is not
            (see TAGS_ROW_MIN_WIDTH). */}
          <Box
            width="100%"
            display="flex"
            flexWrap="wrap"
            alignItems="flex-start"
            justifyContent="space-between"
            columnGap={4}
            rowGap={2}
            css={{ [CAPTURE]: { minHeight: CAPTURE_HEADER_MIN_HEIGHT } }}
          >
            <Heading as="h3" size={"sm"} flex="1 1 auto" minWidth={0}>
              {heading}
            </Heading>
            {hasData && (
              <Box
                data-run-hours-tags
                css={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "8px",
                  marginLeft: "auto",
                  maxWidth: "100%",
                  [WIDE]: { flexDirection: "row", alignItems: "center", gap: "16px" },
                  // PDF export: the full-width card has room for both legs on
                  // one line even though it is narrower than the WIDE breakpoint.
                  [CAPTURE]: { flexDirection: "row", alignItems: "center", gap: "12px" },
                }}
              >
                {presentLegs.map((leg, index) => (
                  <Fragment key={leg}>
                    {index > 0 && <LegDivider />}
                    <HeaderTagGroup leg={leg} stats={stats[leg]} unit={unit} />
                  </Fragment>
                ))}
              </Box>
            )}
          </Box>
          {!hasData ? (
            <Informative heading="No data available" text="No data available for the selected period." />
          ) : (
            <>
              <Chart.Root height="260px" chart={chart} css={CAPTURE_CHART_TEXT_CSS}>
                <BarChart data={chart.data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={chart.color("border.muted")} vertical={false} />
                  {/* Keep a tick on the first and last buckets so the axis always
                    spans the full selected period. */}
                  <XAxis
                    axisLine={false}
                    tickLine={false}
                    dataKey={chart.key("start")}
                    tickFormatter={dateFormatter}
                    interval="preserveStartEnd"
                    tick={{ fill: LABEL_TEXT_COLOR }}
                  />
                  <Tooltip
                    cursor={{ fill: chart.color("bg.muted") }}
                    animationDuration={0}
                    content={unitTooltip(unit, dateFormatter)}
                  />
                  {/* Pinned to 0 to max(24, tallest stack): a full day is always the
                      minimum scale, and the axis only extends when a bucket exceeds it. */}
                  <YAxis
                    domain={[0, axis.max]}
                    ticks={axis.ticks}
                    allowDecimals={axis.max <= RUN_HOURS_SUB_DAILY_AXIS_MAX}
                    tick={{ fill: LABEL_TEXT_COLOR }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => (unit ? `${value} ${unit}` : `${value}`)}
                  />
                  {chart.series.map((item) => (
                    <Bar
                      key={item.name}
                      stackId={STACK_ID}
                      isAnimationActive={false}
                      dataKey={chart.key(item.name)}
                      fill={chart.color(item.color)}
                      name={String(item.label)}
                    />
                  ))}
                  {/* Same recharts Legend slot (bottom, 28px) the other report
                      charts use, so the legend sits at exactly the same place and
                      the x-axes line up by construction. Custom content keeps the
                      leg icons and lists only the legs that are present. */}
                  <Legend
                    verticalAlign="bottom"
                    height={28}
                    content={() => (
                      <Box
                        as="ul"
                        display="flex"
                        justifyContent="center"
                        alignItems="flex-start"
                        gap={4}
                        width="100%"
                        m={0}
                        p={0}
                        aria-label="Legend"
                      >
                        {presentLegs.map((leg) => (
                          <LegendItem key={leg} leg={leg} />
                        ))}
                      </Box>
                    )}
                  />
                </BarChart>
              </Chart.Root>
            </>
          )}
        </VStack>
      </Box>
    </Box>
  )
}

export default RunHoursChart
